import { createContext, useRef, useEffect, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
// firestore
import { doc, query, addDoc, where, deleteDoc, Timestamp, onSnapshot, collection } from "firebase/firestore";
import { db } from "../services/firebase";
// settings
import actionTypes from "../common/actionTypes";
import { APP_ROUTES, AUTOMATION_TYPES, DATABASE_STATUTES } from "../common/constants";
// context
import useAuthContext from "../hooks/useAuthContext";
// reducers
import { automationTagsReducer } from "../reducers";
// hooks
import useCustomReducer from "../hooks/useCustomReducer";
import {
  processBISConfig,
  processCSConfig,
  processISConfig,
  processPreorderConfig,
} from "./ProductsContext/preorderHelpers";

const AUTOMATION_TAGS = "Automations";

const initialState = {
  data: [],
  filteredData: [],
  status: DATABASE_STATUTES.NOT_LOADED,
  error: null,
};

const AutomationTagsContext = createContext({
  ...initialState,
  createAutomationTag: () => Promise.resolve(),
  deleteAutomationTag: () => Promise.resolve(),
});

const AutomationTagsProvider = ({ children }) => {
  const [state, dispatch] = useCustomReducer(automationTagsReducer, initialState);

  const automationTagsRef = useRef(null);

  const { user } = useAuthContext();

  const { pathname } = useLocation();
  const isAutomationsPage = pathname.startsWith(APP_ROUTES.AUTOMATIONS.ROOT);

  useEffect(() => {
    if (user?.domain && isAutomationsPage) {
      dispatch({
        type: actionTypes.FETCH_AUTOMATION_TAGS,
      });

      automationTagsRef.current = collection(db, AUTOMATION_TAGS);

      try {
        return onSnapshot(
          query(
            automationTagsRef.current,
            where("type", "==", AUTOMATION_TYPES.TAGS),
            where("shopId", "==", user.domain)
          ),
          (snapshot) => {
            dispatch({
              type: actionTypes.FETCH_AUTOMATION_TAGS_SUCCESS,
              payload: snapshot.docs.map((doc) => ({
                ...doc.data(),
                id: doc.id,
              })),
            });
          }
        );
      } catch (error) {
        dispatch({
          type: actionTypes.FETCH_AUTOMATION_TAGS_FAILURE,
          payload: error,
        });
      }
    }
  }, [dispatch, user?.domain, isAutomationsPage]);

  const createAutomationTag = useCallback(
    async (tag) => {
      try {
        dispatch({
          type: actionTypes.CREATE_AUTOMATION_TAG,
        });

        const { settings, ...rest } = tag;

        const processedSettings = {
          config: processPreorderConfig(settings.config),
          configBIS: processBISConfig(settings.configBIS),
          configCS: processCSConfig(settings.configCS),
          configIS: processISConfig(settings.configIS),
        };

        await addDoc(automationTagsRef.current, {
          ...rest,
          settings: {
            ...settings,
            ...processedSettings,
          },
          createdAt: Timestamp.now(),
          products: 0,
          shopId: user.domain,
          type: AUTOMATION_TYPES.TAGS,
        });

        dispatch({
          type: actionTypes.CREATE_AUTOMATION_TAG_SUCCESS,
        });

        return Promise.resolve();
      } catch (error) {
        dispatch({
          type: actionTypes.CREATE_AUTOMATION_TAG_FAILURE,
          payload: error,
        });

        return Promise.reject(error);
      }
    },
    [dispatch, user?.domain]
  );

  const deleteAutomationTag = useCallback(
    async (id) => {
      try {
        dispatch({
          type: actionTypes.DELETE_AUTOMATION_TAG,
        });

        await deleteDoc(doc(automationTagsRef.current, id));

        dispatch({
          type: actionTypes.DELETE_AUTOMATION_TAG_SUCCESS,
        });
      } catch (error) {
        dispatch({
          type: actionTypes.DELETE_AUTOMATION_TAG_FAILURE,
          payload: error,
        });
      }
    },
    [dispatch]
  );

  const searchTagByName = useCallback(
    async (name, domain) => {
      try {
        dispatch({
          type: actionTypes.SEARCH_TAGS,
        });

        if (!name) {
          dispatch({
            type: actionTypes.SEARCH_TAGS_SUCCESS,
            payload: [],
          });

          return;
        }

        return onSnapshot(
          query(
            automationTagsRef.current,
            where("type", "==", AUTOMATION_TYPES.TAGS),
            where("shopId", "==", domain),
            where("name", ">=", name),
            where("name", "<=", `${name}~`)
          ),
          (snapshot) => {
            dispatch({
              type: actionTypes.SEARCH_TAGS_SUCCESS,
              payload: snapshot.docs.map((doc) => ({
                ...doc.data(),
                id: doc.id,
              })),
            });
          }
        );
      } catch (error) {
        dispatch({
          type: actionTypes.SEARCH_TAGS_FAILURE,
          payload: error,
        });
      }
    },
    [dispatch]
  );

  const memoizedState = useMemo(
    () => ({
      ...state,
      createAutomationTag,
      deleteAutomationTag,
      searchTagByName,
    }),
    [state, createAutomationTag, deleteAutomationTag, searchTagByName]
  );

  return <AutomationTagsContext.Provider value={memoizedState}>{children}</AutomationTagsContext.Provider>;
};

export { AutomationTagsContext, AutomationTagsProvider };
